import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/v1';
const STORAGE_KEY = 'signature_operator_token';

export function getOperatorToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearOperatorToken(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // stockage indisponible : rien a effacer
  }
}

export const operatorApi = axios.create({ baseURL: `${API_BASE_URL}/operator`, timeout: 30000 });

operatorApi.interceptors.request.use((request) => {
  const token = getOperatorToken();
  if (token) request.headers.Authorization = `Bearer ${token}`;
  return request;
});

operatorApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !String(error.config?.url).endsWith('/login')) {
      clearOperatorToken();
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

export async function operatorLogin(password: string): Promise<void> {
  const response = await operatorApi.post('/login', { password });
  sessionStorage.setItem(STORAGE_KEY, response.data.token);
}

export function errorMessage(error: unknown): string {
  const e = error as { response?: { data?: { error?: { message?: string } } }; message?: string };
  return e.response?.data?.error?.message || e.message || 'Une erreur est survenue';
}

/** Charge une ressource et expose son etat ; `reload` relance l'appel. */
export function useFetch<T>(loader: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(loader, deps);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await load());
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, error, loading, reload };
}

/** Tarif au volume : meme calcul que le serveur (services/credits.ts), pour l'apercu avant validation. */
export interface TariffTier {
  up_to: number | null;
  unit_cents: number;
}

export function priceForQuantity(tiers: TariffTier[], quantity: number): number {
  const qty = Math.max(0, Math.floor(quantity));
  const tier = tiers.find((t) => t.up_to === null || qty <= t.up_to) ?? tiers[tiers.length - 1];
  return Math.round(qty * (tier?.unit_cents ?? 0));
}

// ---------------------------------------------------------------- types de l'API operateur
export interface ClientRow {
  id: string;
  name: string;
  email: string;
  contact_name: string | null;
  phone: string | null;
  notes: string | null;
  is_active: boolean;
  billing_mode: 'free' | 'prepaid';
  low_credit_threshold: number;
  created_at: string;
  balance: number;
  committed: number;
  available: number;
  documents_total: number;
  signatures_total: number;
  signatures_30d: number;
  last_document_at: string | null;
  active_keys: number;
}

export interface SeriesPoint {
  day: string;
  documents: number;
  signatures: number;
  sales_cents: number;
}

export interface LedgerEntry {
  id: string;
  kind: 'purchase' | 'grant' | 'consumption' | 'adjustment';
  credits: number;
  amount_cents: number | null;
  reference: string | null;
  note: string | null;
  created_at: string;
  document_id?: string | null;
  document_title?: string | null;
  client_id?: string;
  client?: string;
}

export interface DocumentRow {
  id: string;
  title: string;
  status: string;
  created_at: string;
  sent_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
  signature_style: string;
  client_id: string;
  client: string;
  signers_total: number;
  signers_signed: number;
}

export interface Overview {
  days: number;
  clients: { total: number; active: number; prepaid: number };
  documents: { by_status: { status: string; count: number }[]; completion_rate: number | null };
  period: {
    documents_created: number;
    documents_completed: number;
    signatures: number;
    signatures_previous: number;
    signatures_trend: number | null;
    sms_signatures: number;
    documents_declined: number;
  };
  money: {
    sales_period_cents: number;
    sales_total_cents: number;
    credits_sold: number;
    credits_consumed_period: number;
    credits_consumed_total: number;
    credits_granted: number;
    credits_outstanding: number;
    average_price_cents: number;
    recognized_revenue_cents: number;
    estimated_costs_cents: number;
    estimated_margin_cents: number;
  };
  series: SeriesPoint[];
  top_clients: { id: string; name: string; signatures: number }[];
  attention: {
    sealing_stuck: { id: string; title: string; client: string }[];
    expiring_soon: number;
    failed_webhooks_24h: number;
    email_suppressions: { total: number; recent: number } | null;
    low_balance: { id: string; name: string; balance: number; available: number; threshold: number }[];
    negative_balance: { id: string; name: string; balance: number }[];
  };
  recent_activity: { type: string; occurred_at: string; document_id: string; title: string; client: string }[];
}
